import EventableObject from './eventable_object';
import Settings from './settings';
import PsdExporter from './psd_exporter';
import DataBlock from './data_block';

import ImageLayer from './layers/image_layer';
import CleanLayer from './layers/clean_layer';

export default class Page extends EventableObject {
  constructor(filename, name) {
    super();

    if (!name)
      name = filename;
    if (!name)
      name = '';

    this.ready = false;

    this.name = name.split('/').pop().split('?').shift().replace(/\.[^\.]*$/, '');

    this.width = 0;
    this.height = 0;

    this.layers = [];
    this.layerIndex = 1; // для названий слоев
    this.layerId = 1; // для id слоев
    this.selection = null;
    this.selectedLayer = null;

    this.source = new Image();
    this.source.crossOrigin = 'Anonymous';
    this.source.addEventListener('load', ()=>{
      this.width = this.source.width;
      this.height = this.source.height;

      if (this.layers.length == 0) {
        this.sourceLayer = new ImageLayer(this, this.source);
        this.sourceLayer.name = 'Source';
        this.sourceLayer.id = this.layerId++;
        this.layers = [this.sourceLayer];
      }

      this.generateSourceValues();

      this.canvas = document.createElement('canvas');
      this.canvas.width = this.width;
      this.canvas.height = this.height;
      this.context = this.canvas.getContext('2d');

      this.overlay = document.createElement('canvas');
      this.overlay.width = this.width;
      this.overlay.height = this.height;
      this.overlayContext = this.overlay.getContext('2d');

      this.update();

      this.ready = true;
      this.triggerEvent('load');
      this.triggerEvent('ready', this.ready);
    });
    if (filename !== undefined) {
      this.source.src = filename;
    }
  }

  addLayer(layer, silent) {
    layer.id = this.layerId++;
    this.layers.unshift(layer);
    this.update();

    if (!silent) {
      this.triggerEvent('update');
    }
  }

  update() {
    let ctx = this.context;

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1.0;
    ctx.clearRect(0, 0, this.width, this.height);

    for (let i = this.layers.length - 1; i >= 0; i--) {
      let layer = this.layers[i];
      layer.render(ctx);
    }
  }

  render(canvas) {
    if (!this.canvas)
      return;

    canvas.width = this.width;
    canvas.height = this.height;

    let ctx = canvas.getContext('2d');

    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1.0;
    ctx.clearRect(0, 0, this.width, this.height);

    ctx.drawImage(this.canvas, 0, 0);
    ctx.drawImage(this.overlay, 0, 0);
  }

  cleanAt(x, y) {
    if (!this.ready)
      return;

    this.ready = false;
    this.triggerEvent('ready', this.ready);

    setImmediate(() => {
      let layer = new CleanLayer(this, x, y);
      layer.name = 'Clean #' + this.layerIndex++;

      this.addLayer(layer);

      this.update();

      this.ready = true;
      this.triggerEvent('ready', this.ready);
    });
  }

  crosshairAt(x, y) {
    let ctx = this.overlayContext;
    ctx.clearRect(0, 0, this.width, this.height);

    if (!Number.isInteger(x))
      return false;

    if (this.selection && !(
      x >= this.selection.left && y >= this.selection.top &&
      x <= this.selection.right && y <= this.selection.bottom
    ))
      return false;

    let layer = null;
    let values = null;
    if (this.prevCrosshairLayer && (this.prevCrosshairLayer in this.layers)) {
      layer = this.prevCrosshairLayer;
      if (layer.canvas && x >= layer.x && y >= layer.y && x < layer.x + layer.width && y < layer.y + layer.height) {
        values = layer.canvas.getContext('2d').getImageData(x - layer.x, y - layer.y, 1, 1).data;
        // альфа-значение слоя в точке
        if (values[3] <= 8) {
          layer = null;
        }
      } else {
        layer = null;
      }
    }

    if (!layer) {
      layer = this.getLayerAt(x, y);
      values = null;
    }

    if (!layer || layer.type != 'clean')
      return false;

    this.prevCrosshairLayer = layer;

    if (!values) {
      values = layer.canvas.getContext('2d').getImageData(x - layer.x, y - layer.y, 1, 1).data;
    }

    let value = Math.max(values[0], values[1], values[2]);
    ctx.strokeStyle = (value < 128 ? 'white' : 'black');
    ctx.lineWidth = 1;

    let rectangle = {
      left: x,
      top: y,
      right: x,
      bottom: y
    };

    while (rectangle.left >= 0 && layer.mask[y * this.width + rectangle.left])
      rectangle.left -= 1;
    while (rectangle.right < this.width && layer.mask[y * this.width + rectangle.right])
      rectangle.right += 1;
    while (rectangle.top >= 0 && layer.mask[rectangle.top * this.width + x])
      rectangle.top -= 1;
    while (rectangle.bottom < this.height && layer.mask[rectangle.bottom * this.width + x])
      rectangle.bottom += 1;

    ctx.beginPath();
    ctx.moveTo(rectangle.left, y);
    ctx.lineTo(rectangle.right, y);
    ctx.moveTo(x, rectangle.top);
    ctx.lineTo(x, rectangle.bottom);
    ctx.stroke();

    return true;
  }

  updateSelection(selection) {
    this.selection = selection;
    this.selectedLayer = null;
  }

  selectLayer(layer) {
    if (Number.isInteger(layer)) {
      layer = this.layers[layer];
    }

    if (this.selectedLayer == layer) {
      if (this.selection || !layer) {
        this.selection = null;
      } else {
        this.selection = {
          left: layer.x,
          top: layer.y,
          right: layer.x + layer.width,
          bottom: layer.y + layer.height
        }

        if (layer.type == 'text') {
          setImmediate(() => {
            document.getElementById('textLayerContent').focus();
          });
        }
      }
      this.triggerEvent('update');
      return;
    }

    this.selectedLayer = layer;

    if (layer === null || layer == this.sourceLayer || (
      layer.x == 0 && layer.y == 0 && layer.width == this.width && layer.height == this.height
    )) {
      this.selection = null;
    } else {
      this.selection = {
        left: layer.x,
        top: layer.y,
        right: layer.x + layer.width,
        bottom: layer.y + layer.height
      }

      if (layer.type == 'text') {
        setImmediate(() => {
          document.getElementById('textLayerContent').focus();
        });
      }
    }

    this.triggerEvent('update');
  }

  generateSourceValues() {
    let sourceCtx = this.sourceLayer.canvas.getContext('2d');
    this.sourceData = sourceCtx.getImageData(0, 0, this.width, this.height);

    // для значений используется компонента V из HSV
    // при переводе из RGB в HSV ее очень просто получить - 
    // она равна максимальному из значений R, G или B
    this.sourceValues = new Uint8Array(this.width * this.height);
    for (let i = 0, j = 0; i < this.sourceValues.length; i++, j += 4) {
      this.sourceValues[i] = Math.max(this.sourceData.data[j], this.sourceData.data[j + 1], this.sourceData.data[j + 2]);
    }

//    const ACCURACY = Settings.get('clean-accuracy', 10)|0;
//    this.generateZones(ACCURACY);
  }

  findLayer(name) {
    return this.layers.find(layer => layer.name == name);
  }

  // exceptLayer - слой, который не нужно трогать. сделано для поиска нужного цвета для текстовых слоев
  getLayerAt(x, y, exceptLayer) {
    return this.layers.find(layer => {
      if (layer != exceptLayer && layer.canvas && x >= layer.x && y >= layer.y && x < layer.x + layer.width && y < layer.y + layer.height) {
        if (layer.type == 'text') {
          return layer;
        }

        let values = layer.canvas.getContext('2d').getImageData(x - layer.x, y - layer.y, 1, 1).data;
        // альфа-значение слоя в точке
        if (values[3] > 8) {
          return layer;
        }
      }
    });
  }

  // silent - без вызова события update
  removeLayer(layer, silent) {
    if (Number.isInteger(layer)) {
      layer = this.layers[layer];
    }

    if (!layer)
      return;

    this.layers = this.layers.filter(x => x != layer);
    if (layer == this.selectedLayer) {
      this.selectedLayer = null;
      this.selection = null;
    }

    if (!silent) {
      this.update();
      this.triggerEvent('update');
    }
  }

  mergeCleanLayers() {
    if (!this.ready)
      return;

    this.ready = false;
    this.triggerEvent('ready', this.ready);

    setImmediate(() => {
      let merged = new CleanLayer(this);
      merged.name = 'Clean';
      merged.id = this.layerId++;
      merged.x = 0;
      merged.y = 0;
      merged.width = this.width;
      merged.height = this.height;
      merged.canvas = document.createElement('canvas');
      merged.canvas.width = this.width;
      merged.canvas.height = this.height;
      merged.mask = new Uint8Array(this.width * this.height);

      let ctx = merged.canvas.getContext('2d');

      for (let i = this.layers.length - 1; i >= 0; i--) {
        let layer = this.layers[i];
        if (layer.type == 'clean') {
          ctx.drawImage(layer.canvas, layer.x, layer.y, layer.width, layer.height);

          for (let q = 0; q < merged.mask.length; q++) {
            merged.mask[q] |= layer.mask[q];
          }
        }
      }

      this.layers = this.layers.filter(x => x.type != 'clean');
      this.layers.splice(-1, 0, merged);

      this.ready = true;
      this.triggerEvent('ready', this.ready);
    });
  }

  exportToPsd() {
    let exporter = new PsdExporter();
    return exporter.export(this);
  }

  // поиск зон внутри изображения
  // останется на будущее
  generateZones(delta) {
    let values = this.sourceValues;
    let zones = new Uint32Array(values.length);
    let zoneIndex = 1;
    let zoneTransforms = {};
    let currValue, prevValue;
    let currZone, prevZone;
    let zoneUp, zoneLeft;
    let deltaUp, deltaLeft;
    for (let i = 0; i < values.length; i++) {
      currValue = values[i];

      if (i >= this.width) {
        deltaUp = Math.abs(values[i - this.width] - currValue);

        if (deltaUp <= delta) {
          zoneUp = zones[i - this.width];

          while (zoneUp in zoneTransforms) {
            zoneUp = zoneTransforms[zoneUp];
          }
        } else {
          zoneUp = 0;
        }
      } else {
        zoneUp = 0;
      }

      if (i % this.width > 0) {
        deltaLeft = Math.abs(prevValue - currValue);
        if (deltaLeft <= delta) {
          zoneLeft = prevZone;
        } else {
          zoneLeft = 0;
        }
      } else {
        zoneLeft = 0;
      }

      if (zoneUp) {
        if (zoneLeft) {
          currZone = zoneLeft;
          if (zoneUp != zoneLeft) {
            zoneTransforms[zoneUp] = zoneLeft;
          }
        } else {
          currZone = zoneUp;
        }
      }
      else if (zoneLeft) {
        currZone = zoneLeft;
      }
      else {
        currZone = zoneIndex++;
      }

      zones[i] = currZone;

      prevValue = currValue;
      prevZone = currZone;
    }

    for (let i = 0; i < zones.length; i++) {
      while (zones[i] in zoneTransforms) {
        zones[i] = zoneTransforms[zones[i]];
      }
    }

    this.zones = zones;
  }

  exportBinary() {
    let data = new DataBlock();

    data.put4(this.id);
    data.putString(this.name);
    data.putDouble(this.width, this.height);
    data.put4(this.sourceLayer.id);
    data.put4(this.selectedLayer ? this.selectedLayer.id : 0);

    if (this.savedViewportState) {
      data.putDouble(this.savedViewportState.offset.x, this.savedViewportState.offset.y);
      data.putDouble(this.savedViewportState.scale);
    } else {
      data.putDouble(0, 0, 1.0);
    }
    if (this.selection) {
      data.put(1);
      data.putDouble(this.selection.left, this.selection.top, this.selection.right, this.selection.bottom);
    } else {
      data.put(0);
      data.putDouble(0, 0, this.width, this.height);
    }

    data.put4(this.layers.length);
    this.layers.forEach(layer => {
      let layerData = layer.exportBinary();
      data.put4(layerData.length);
      data.put(layerData);
    });

    return data;
  }
}
